/**
 * features/platform/hooks — hooks de leitura e mutação da área de plataforma.
 *
 * Business Units: CRUD completo via TanStack Query + platformRepo.
 * Stages: CRUD + reordenação por BU via TanStack Query + platformRepo.
 *   Parametrizados por pipelineKind: "commercial" | "partnership".
 *   - "commercial" → /business-units/{buId}/stages (pipeline comercial)
 *   - "partnership" → /business-units/{buId}/partnership-stages (pipeline de parcerias)
 *   Query keys distintas garantem isolamento de cache entre os dois pipelines.
 * Branding: leitura + salvamento (com suporte a upload de logo).
 *
 * Padrões seguidos: admin/hooks.ts, accounts/hooks.ts.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTenantQuery } from "../../app/providers/QueryProvider.js";
import {
  listBusinessUnits,
  createBusinessUnit,
  renameBusinessUnit,
  deactivateBusinessUnit,
  removeBusinessUnit,
  listStages,
  addStage,
  reorderStages,
  removeStage,
  updateStage,
  listPartnershipStages,
  addPartnershipStage,
  reorderPartnershipStages,
  removePartnershipStage,
  updatePartnershipStage,
  getBranding,
  updateBranding,
  uploadLogo,
  getTenant,
  getPublicBrandJson,
  listBuMembers,
  listUsers,
  assignMembership,
  changeMembershipRole,
  removeMembership,
  listOriginChannelsAdmin,
  addOriginChannel,
  deactivateOriginChannel,
  reactivateOriginChannel,
  renameOriginChannel,
  type PortalBusinessUnit,
  type PortalStage,
  type PortalBranding,
  type PortalPublicBranding,
  type PortalTenant,
  type PortalBuMember,
  type PortalOriginChannel,
  type PortalOriginChannelAdmin,
  type AddOriginChannelInput,
  type PortalUser,
  type MembershipRole,
  type UpdateBrandingInput,
  type CreateBusinessUnitInput,
  type RenameBusinessUnitInput,
  type AddStageInput,
  type ReorderStagesInput,
  type UpdateStageInput,
} from "../../data/repos/platformRepo.js";

// ---------------------------------------------------------------------------
// Tipo discriminador de pipeline
// ---------------------------------------------------------------------------

/** Tipo de pipeline suportado pela StagesSection e pelos hooks de stages. */
export type PipelineKind = "commercial" | "partnership";

// ---------------------------------------------------------------------------
// Business Units — leitura
// ---------------------------------------------------------------------------

export interface UseBusinessUnitsResult {
  units: readonly PortalBusinessUnit[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Invalida TODAS as leituras de Business Unit, nas duas chaves que existem para o mesmo dado.
 *
 * A tela de plataforma lê por `useTenantQuery("businessUnits", …)`, cuja chave é
 * `[tenantId, businessUnitId, "businessUnits"]` — a área vem por ÚLTIMO (ver `buildQueryKey`), de
 * modo que invalidar por prefixo NÃO alcança. Por isso o predicado.
 *
 * Invalidar só `["platform", "businessUnits"]`, como se fazia antes, acertava um hook que a tela
 * de plataforma não usa: a lista só mudava depois de um F5 (LDG-0186). Mesmo molde de
 * `invalidateOriginChannels`, logo abaixo — que é justamente por que a aba Canais de origem
 * sempre atualizou e esta não.
 */
function invalidateBusinessUnits(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ["platform", "businessUnits"] });
  void qc.invalidateQueries({
    predicate: (query) => query.queryKey.includes("businessUnits"),
  });
}

export function useBusinessUnits(): UseBusinessUnitsResult {
  // Usa Array<PortalBusinessUnit> (mutável) como tipo TData para compatibilidade com TanStack Query
  const query = useQuery<Array<PortalBusinessUnit>, Error>({
    queryKey: ["platform", "businessUnits"],
    queryFn: async () => {
      const result = await listBusinessUnits();
      return result as Array<PortalBusinessUnit>;
    },
    staleTime: 30_000,
  });

  return {
    units: query.data ?? [],
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// Business Units — criação
// ---------------------------------------------------------------------------

export function useCreateBusinessUnit() {
  const qc = useQueryClient();

  const mutation = useMutation<PortalBusinessUnit, Error, CreateBusinessUnitInput>({
    mutationFn: (input) => createBusinessUnit(input),
    onSuccess: () => {
      invalidateBusinessUnits(qc);
    },
  });

  return {
    createUnit: (input: CreateBusinessUnitInput) => mutation.mutateAsync(input),
    isCreating: mutation.isPending,
    createError: mutation.isError ? (mutation.error?.message ?? "Erro ao criar unidade de negócio.") : null,
    createSuccess: mutation.isSuccess,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Business Units — renomear
// ---------------------------------------------------------------------------

export function useRenameBusinessUnit() {
  const qc = useQueryClient();

  const mutation = useMutation<void, Error, RenameBusinessUnitInput>({
    mutationFn: (input) => renameBusinessUnit(input),
    onSuccess: () => {
      invalidateBusinessUnits(qc);
    },
  });

  return {
    renameUnit: (input: RenameBusinessUnitInput) => mutation.mutateAsync(input),
    isRenaming: mutation.isPending,
    renameError: mutation.isError ? (mutation.error?.message ?? "Erro ao renomear unidade de negócio.") : null,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Business Units — desativar
// ---------------------------------------------------------------------------

export function useDeactivateBusinessUnit() {
  const qc = useQueryClient();

  const mutation = useMutation<void, Error, string>({
    mutationFn: (id) => deactivateBusinessUnit(id),
    onSuccess: () => {
      invalidateBusinessUnits(qc);
    },
  });

  return {
    deactivateUnit: (id: string) => mutation.mutateAsync(id),
    isDeactivating: mutation.isPending,
    deactivateError: mutation.isError ? (mutation.error?.message ?? "Erro ao desativar unidade de negócio.") : null,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Business Units — remover (hard delete condicional)
// ---------------------------------------------------------------------------

export function useRemoveBusinessUnit() {
  const qc = useQueryClient();

  const mutation = useMutation<void, Error, string>({
    mutationFn: (id) => removeBusinessUnit(id),
    onSuccess: () => {
      invalidateBusinessUnits(qc);
    },
  });

  return {
    removeUnit: (id: string) => mutation.mutateAsync(id),
    isRemoving: mutation.isPending,
    removeError: mutation.isError ? (mutation.error?.message ?? "Erro ao remover unidade de negócio.") : null,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Stages — helpers internos para selecionar funções do repo por pipelineKind
// ---------------------------------------------------------------------------

/**
 * Retorna a chave de cache para stages de acordo com o tipo de pipeline.
 * "commercial"  → ["platform", "stages", buId]
 * "partnership" → ["platform", "partnership-stages", buId]
 */
function stagesQueryKey(kind: PipelineKind, buId: string): readonly unknown[] {
  return kind === "commercial"
    ? ["platform", "stages", buId]
    : ["platform", "partnership-stages", buId];
}

function resolveListStagesFn(kind: PipelineKind) {
  return kind === "commercial" ? listStages : listPartnershipStages;
}

function resolveAddStageFn(kind: PipelineKind) {
  return kind === "commercial" ? addStage : addPartnershipStage;
}

function resolveReorderStagesFn(kind: PipelineKind) {
  return kind === "commercial" ? reorderStages : reorderPartnershipStages;
}

function resolveRemoveStageFn(kind: PipelineKind) {
  return kind === "commercial" ? removeStage : removePartnershipStage;
}

function resolveUpdateStageFn(kind: PipelineKind) {
  return kind === "commercial" ? updateStage : updatePartnershipStage;
}

// ---------------------------------------------------------------------------
// Stages — leitura por BU
// ---------------------------------------------------------------------------

export interface UseStagesResult {
  stages: readonly PortalStage[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook de leitura de estágios parametrizado por tipo de pipeline.
 *
 * @param buId - ID da Business Unit (null/undefined desabilita a query).
 * @param kind - Tipo de pipeline: "commercial" (padrão) ou "partnership".
 *
 * Pipeline comercial: pode retornar ORG-ERR-016 (bug conhecido AllowAnonymous).
 * Pipeline de parcerias: autenticado corretamente; erros são reais.
 */
export function useStages(
  buId: string | null | undefined,
  kind: PipelineKind = "commercial",
): UseStagesResult {
  const safeBuId = buId ?? "";
  const query = useQuery<Array<PortalStage>, Error>({
    queryKey: stagesQueryKey(kind, safeBuId),
    queryFn: async () => {
      const result = await resolveListStagesFn(kind)(safeBuId);
      return result as Array<PortalStage>;
    },
    enabled: !!buId,
    staleTime: 30_000,
    // Comercial: não retentar — 404 é bug conhecido de AllowAnonymous.
    // Parceria: não retentar também, para não mascarar erro real na UI.
    retry: false,
  });

  return {
    stages: query.data ?? [],
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
    refetch: () => { void query.refetch(); },
  };
}

// ---------------------------------------------------------------------------
// Stages — adicionar
// ---------------------------------------------------------------------------

/**
 * Hook de adição de estágio parametrizado por tipo de pipeline.
 *
 * @param buId - ID da Business Unit.
 * @param kind - Tipo de pipeline: "commercial" (padrão) ou "partnership".
 */
export function useAddStage(
  buId: string | null | undefined,
  kind: PipelineKind = "commercial",
) {
  const qc = useQueryClient();
  const safeBuId = buId ?? "";

  const mutation = useMutation<void, Error, AddStageInput>({
    mutationFn: (input) => resolveAddStageFn(kind)(safeBuId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stagesQueryKey(kind, safeBuId) });
    },
  });

  return {
    addStage: (input: AddStageInput) => mutation.mutateAsync(input),
    isAdding: mutation.isPending,
    addError: mutation.isError ? (mutation.error?.message ?? "Erro ao adicionar estágio.") : null,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Stages — reordenar
// ---------------------------------------------------------------------------

/**
 * Hook de reordenação de estágios parametrizado por tipo de pipeline.
 *
 * @param buId - ID da Business Unit.
 * @param kind - Tipo de pipeline: "commercial" (padrão) ou "partnership".
 */
export function useReorderStages(
  buId: string | null | undefined,
  kind: PipelineKind = "commercial",
) {
  const qc = useQueryClient();
  const safeBuId = buId ?? "";

  const mutation = useMutation<void, Error, ReorderStagesInput>({
    mutationFn: (input) => resolveReorderStagesFn(kind)(safeBuId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stagesQueryKey(kind, safeBuId) });
    },
  });

  return {
    reorder: (input: ReorderStagesInput) => mutation.mutateAsync(input),
    isReordering: mutation.isPending,
    reorderError: mutation.isError ? (mutation.error?.message ?? "Erro ao reordenar estágios.") : null,
  };
}

// ---------------------------------------------------------------------------
// Stages — remover
// ---------------------------------------------------------------------------

/**
 * Hook de remoção de estágio parametrizado por tipo de pipeline.
 *
 * @param buId - ID da Business Unit.
 * @param kind - Tipo de pipeline: "commercial" (padrão) ou "partnership".
 */
export function useRemoveStage(
  buId: string | null | undefined,
  kind: PipelineKind = "commercial",
) {
  const qc = useQueryClient();
  const safeBuId = buId ?? "";

  const mutation = useMutation<void, Error, string>({
    mutationFn: (stageId) => resolveRemoveStageFn(kind)(safeBuId, stageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stagesQueryKey(kind, safeBuId) });
    },
  });

  return {
    removeStage: (stageId: string) => mutation.mutateAsync(stageId),
    isRemoving: mutation.isPending,
    removeError: mutation.isError ? (mutation.error?.message ?? "Erro ao remover estágio.") : null,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Stages — editar atributos
// ---------------------------------------------------------------------------

/**
 * Hook de edição de atributos de estágio parametrizado por tipo de pipeline.
 *
 * @param buId - ID da Business Unit.
 * @param kind - Tipo de pipeline: "commercial" (padrão) ou "partnership".
 */
export function useUpdateStage(
  buId: string | null | undefined,
  kind: PipelineKind = "commercial",
) {
  const qc = useQueryClient();
  const safeBuId = buId ?? "";

  const mutation = useMutation<void, Error, UpdateStageInput>({
    mutationFn: (input) => resolveUpdateStageFn(kind)(safeBuId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: stagesQueryKey(kind, safeBuId) });
    },
  });

  return {
    updateStage: (input: UpdateStageInput) => mutation.mutateAsync(input),
    isUpdating: mutation.isPending,
    updateError: mutation.isError ? (mutation.error?.message ?? "Erro ao editar estágio.") : null,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Memberships (membros por BU)
// ---------------------------------------------------------------------------

export interface UseBuMembersResult {
  members: readonly PortalBuMember[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useBuMembers(buId: string | null | undefined): UseBuMembersResult {
  const query = useQuery<Array<PortalBuMember>, Error>({
    queryKey: ["platform", "buMembers", buId ?? ""],
    queryFn: async () => (await listBuMembers(buId ?? "")) as Array<PortalBuMember>,
    enabled: !!buId,
    staleTime: 30_000,
  });

  return {
    members: query.data ?? [],
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
    refetch: () => { void query.refetch(); },
  };
}

export function useUsers() {
  const query = useQuery<Array<PortalUser>, Error>({
    queryKey: ["platform", "users"],
    queryFn: async () => (await listUsers()) as Array<PortalUser>,
    staleTime: 30_000,
  });

  return {
    users: query.data ?? [],
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
  };
}

function invalidateMemberships(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["platform", "buMembers"] });
  void qc.invalidateQueries({ queryKey: ["platform", "users"] });
}

export function useAssignMembership() {
  const qc = useQueryClient();
  const mutation = useMutation<void, Error, { userId: string; buId: string; role: MembershipRole }>({
    mutationFn: ({ userId, buId, role }) => assignMembership(userId, buId, role),
    onSuccess: () => invalidateMemberships(qc),
  });
  return {
    assign: (userId: string, buId: string, role: MembershipRole) => mutation.mutateAsync({ userId, buId, role }),
    isAssigning: mutation.isPending,
    assignError: mutation.isError ? (mutation.error?.message ?? "Erro ao vincular membro.") : null,
    reset: () => mutation.reset(),
  };
}

export function useChangeMembershipRole() {
  const qc = useQueryClient();
  const mutation = useMutation<void, Error, { userId: string; buId: string; role: MembershipRole }>({
    mutationFn: ({ userId, buId, role }) => changeMembershipRole(userId, buId, role),
    onSuccess: () => invalidateMemberships(qc),
  });
  return {
    changeRole: (userId: string, buId: string, role: MembershipRole) => mutation.mutateAsync({ userId, buId, role }),
    isChanging: mutation.isPending,
    changeError: mutation.isError ? (mutation.error?.message ?? "Erro ao alterar papel.") : null,
    reset: () => mutation.reset(),
  };
}

export function useRemoveMembership() {
  const qc = useQueryClient();
  const mutation = useMutation<void, Error, { userId: string; buId: string }>({
    mutationFn: ({ userId, buId }) => removeMembership(userId, buId),
    onSuccess: () => invalidateMemberships(qc),
  });
  return {
    remove: (userId: string, buId: string) => mutation.mutateAsync({ userId, buId }),
    isRemoving: mutation.isPending,
    removeError: mutation.isError ? (mutation.error?.message ?? "Erro ao desvincular membro.") : null,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Branding — leitura
// ---------------------------------------------------------------------------

export interface UseBrandingResult {
  branding: PortalBranding | null;
  loading: boolean;
  error: string | null;
}

export function useBranding(): UseBrandingResult {
  const query = useQuery<PortalBranding, Error>({
    queryKey: ["platform", "branding"],
    queryFn: () => getBranding(),
    staleTime: 60_000,
  });

  return {
    branding: query.data ?? null,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
  };
}

// ---------------------------------------------------------------------------
// Tenant — leitura (identificador exibido na aba Branding, DD-008)
// ---------------------------------------------------------------------------

export interface UseTenantResult {
  tenant: PortalTenant | null;
  loading: boolean;
  error: string | null;
}

/**
 * Lê o tenant corrente (id, slug, displayName, ...). `BrandingSection` usa o `slug` para
 * exibir o identificador do tenant na própria aba (DD-008 — sai do topo da página numa task
 * posterior, TASK-12). Chave própria (`["platform", "tenant"]`), independente da que
 * `PlatformPage` usa via `useTenantQuery("tenants", ...)` — mantém `BrandingSection`
 * autocontida, no mesmo padrão das demais seções (cada uma busca os próprios dados).
 */
export function useTenant(): UseTenantResult {
  const query = useQuery<PortalTenant, Error>({
    queryKey: ["platform", "tenant"],
    queryFn: () => getTenant(),
    staleTime: 60_000,
  });

  return {
    tenant: query.data ?? null,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
  };
}

// ---------------------------------------------------------------------------
// Branding — leitura pública (anônima), para slots de identidade de TODO usuário
// ---------------------------------------------------------------------------

export interface UsePublicBrandingResult {
  branding: PortalPublicBranding | null;
  loading: boolean;
  error: string | null;
}

/**
 * Achado da revisão adversarial: `useBranding()` acima (GET /v1/tenant/branding) exige
 * `RequiresTenantAdmin` — um Viewer (a maioria dos usuários) recebia 403 em toda página, e o
 * logo/cores do tenant nunca apareciam para ele nem na sidebar nem no seletor de tenant. Este
 * hook alimenta os slots de identidade que TODO usuário autenticado precisa ver a partir do
 * endpoint público brand.json (`getPublicBrandJson`), que não depende de papel nenhum.
 *
 * Resolve o slug via `useTenantQuery("tenants", getTenant)` — a MESMA chave que Sidebar/
 * TenantModal/PlatformPage já usam para o tenant corrente, então não gera uma segunda
 * requisição: o React Query deduplica por chave.
 */
export function usePublicBranding(): UsePublicBrandingResult {
  const { data: tenant } = useTenantQuery<PortalTenant>("tenants", getTenant);
  const slug = tenant?.slug;

  const query = useTenantQuery<PortalPublicBranding | null>(
    "publicBrand",
    () => getPublicBrandJson(slug ?? ""),
    { enabled: Boolean(slug), staleTime: 60_000 },
  );

  return {
    branding: query.data ?? null,
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-PUBLIC-BRAND" : null,
  };
}

// ---------------------------------------------------------------------------
// Branding — salvar
// ---------------------------------------------------------------------------

/**
 * Predicado de invalidação das consultas de branding.
 *
 * Por que não `queryKey: ["platform", "branding"]`, que era o que estava aqui: `buildQueryKey`
 * (data/queryKeys.ts:28-30) monta a chave como `[tenantId, businessUnitId, area]` — o tenant vem
 * PRIMEIRO e a área por ÚLTIMO. O React Query casa filtro de `queryKey` por PREFIXO, e nenhuma
 * chave deste portal começa com a string "platform". As duas invalidações anteriores, portanto,
 * não casavam consulta alguma: eram no-op, e a tela de Plataforma só parecia funcionar porque
 * `PlatformPage` chama `brandingQuery.refetch()` explicitamente na própria chave.
 *
 * O sintoma que isso produz é exatamente o relatado: o card da página atualiza e a barra lateral
 * continua exibindo o logo antigo (ou as iniciais), porque `publicBrand` tem `staleTime` de 60 s
 * e ninguém a invalida.
 *
 * Casar pela ÁREA (índice 2) cobre as duas consultas de branding de todos os escopos de BU do
 * tenant corrente, que é o que o save e o upload precisam derrubar.
 */
function consultaDeBranding(consulta: { queryKey: readonly unknown[] }): boolean {
  const area = consulta.queryKey[2];
  return area === "branding" || area === "publicBrand";
}

export function useSaveBranding() {
  const qc = useQueryClient();

  const mutation = useMutation<PortalBranding, Error, UpdateBrandingInput>({
    mutationFn: (input) => updateBranding(input),
    onSuccess: () => {
      void qc.invalidateQueries({ predicate: consultaDeBranding });
    },
  });

  return {
    saveBranding: (input: UpdateBrandingInput) => mutation.mutateAsync(input),
    isSaving: mutation.isPending,
    saveError: mutation.isError ? (mutation.error?.message ?? "Erro ao salvar branding.") : null,
    saveSuccess: mutation.isSuccess,
  };
}

// ---------------------------------------------------------------------------
// Branding — upload de logo
// ---------------------------------------------------------------------------

export function useUploadLogo() {
  const qc = useQueryClient();

  const mutation = useMutation<string | null, Error, File>({
    mutationFn: (file) => uploadLogo(file),
    onSuccess: () => {
      void qc.invalidateQueries({ predicate: consultaDeBranding });
    },
  });

  return {
    uploadLogo: (file: File) => mutation.mutateAsync(file),
    isUploading: mutation.isPending,
    uploadError: mutation.isError ? (mutation.error?.message ?? "Erro ao fazer upload do logo.") : null,
    uploadedUrl: mutation.data ?? null,
    reset: () => mutation.reset(),
  };
}

// ---------------------------------------------------------------------------
// Origin Channels — leitura, criação e desativação por BU
// ---------------------------------------------------------------------------

/** Chave de cache dos canais de origem de uma BU (área de plataforma). */
function originChannelsQueryKey(buId: string): readonly unknown[] {
  return ["platform", "originChannels", buId] as const;
}

/**
 * Invalida TODAS as leituras de canais de uma BU: a da área de plataforma
 * (chave acima) e a do seletor de `CreateOpportunityDialog`, que passa por
 * `useTenantQuery` e portanto tem a chave prefixada por tenant/BU
 * (`[tenantId, buId, "opportunity.create.channels", buId]`). Sem isso, um canal
 * recém-criado só apareceria no formulário de oportunidade após o `staleTime`
 * de 30s do QueryClient expirar.
 */
function invalidateOriginChannels(
  qc: ReturnType<typeof useQueryClient>,
  buId: string,
): void {
  void qc.invalidateQueries({ queryKey: originChannelsQueryKey(buId) });
  void qc.invalidateQueries({
    predicate: (query) =>
      query.queryKey.includes("opportunity.create.channels"),
  });
}

export interface UseOriginChannelsResult {
  channels: readonly PortalOriginChannelAdmin[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Lê TODOS os canais de origem de uma BU, ativos e desativados.
 *
 * A tela administrativa precisa enxergar o desativado: enquanto ela mostrava só os
 * ativos, quem desativou um canal e quis o de volta não tinha caminho — recriar o
 * nome violava o índice único e devolvia HTTP 500 (LDG-0124). O seletor de nova
 * oportunidade continua usando `listOriginChannels`, que traz só os ativos.
 *
 * @param buId - ID da Business Unit (null/undefined desabilita a query).
 */
export function useOriginChannels(
  buId: string | null | undefined,
): UseOriginChannelsResult {
  const safeBuId = buId ?? "";
  const query = useQuery<Array<PortalOriginChannelAdmin>, Error>({
    queryKey: originChannelsQueryKey(safeBuId),
    queryFn: async () => (await listOriginChannelsAdmin(safeBuId)) as Array<PortalOriginChannelAdmin>,
    enabled: !!buId,
    staleTime: 30_000,
    // Não retentar: a falha precisa ficar visível na seção, não mascarada por
    // uma lista vazia (foi assim que a ausência de canais passou despercebida).
    retry: false,
  });

  return {
    channels: query.data ?? [],
    loading: query.isLoading,
    error: query.isError ? "WEB-ERR-009" : null,
    refetch: () => { void query.refetch(); },
  };
}

export function useAddOriginChannel(buId: string | null | undefined) {
  const qc = useQueryClient();
  const safeBuId = buId ?? "";

  const mutation = useMutation<void, Error, AddOriginChannelInput>({
    mutationFn: (input) => addOriginChannel(safeBuId, input),
    onSuccess: () => { invalidateOriginChannels(qc, safeBuId); },
  });

  return {
    addChannel: (input: AddOriginChannelInput) => mutation.mutateAsync(input),
    isAdding: mutation.isPending,
    addError: mutation.isError ? (mutation.error?.message ?? "Erro ao adicionar canal de origem.") : null,
    reset: () => mutation.reset(),
  };
}

export function useReactivateOriginChannel(buId: string | null | undefined) {
  const qc = useQueryClient();
  const safeBuId = buId ?? "";

  const mutation = useMutation<void, Error, string>({
    mutationFn: (channelId) => reactivateOriginChannel(safeBuId, channelId),
    onSuccess: () => { invalidateOriginChannels(qc, safeBuId); },
  });

  return {
    reactivateChannel: (channelId: string) => mutation.mutateAsync(channelId),
    isReactivating: mutation.isPending,
    reactivateError: mutation.isError
      ? (mutation.error?.message ?? "Erro ao reativar canal de origem.")
      : null,
    reset: () => mutation.reset(),
  };
}

/**
 * Renomeia um canal de origem da BU.
 *
 * Renomear é o caminho de correção que faltava: sem ele, a única saída para um nome errado era
 * desativar e criar outro, o que troca o id e desliga o rótulo das oportunidades já registradas.
 */
export function useRenameOriginChannel(buId: string | null | undefined) {
  const qc = useQueryClient();
  const safeBuId = buId ?? "";

  const mutation = useMutation<void, Error, { channelId: string; name: string }>({
    mutationFn: ({ channelId, name }) => renameOriginChannel(safeBuId, channelId, name),
    onSuccess: () => { invalidateOriginChannels(qc, safeBuId); },
  });

  return {
    renameChannel: (channelId: string, name: string) => mutation.mutateAsync({ channelId, name }),
    isRenaming: mutation.isPending,
    renameError: mutation.isError
      ? (mutation.error?.message ?? "Erro ao renomear canal de origem.")
      : null,
    reset: () => mutation.reset(),
  };
}

export function useDeactivateOriginChannel(buId: string | null | undefined) {
  const qc = useQueryClient();
  const safeBuId = buId ?? "";

  const mutation = useMutation<void, Error, string>({
    mutationFn: (channelId) => deactivateOriginChannel(safeBuId, channelId),
    onSuccess: () => { invalidateOriginChannels(qc, safeBuId); },
  });

  return {
    deactivateChannel: (channelId: string) => mutation.mutateAsync(channelId),
    isDeactivating: mutation.isPending,
    deactivateError: mutation.isError
      ? (mutation.error?.message ?? "Erro ao desativar canal de origem.")
      : null,
    reset: () => mutation.reset(),
  };
}
